import { BaseEntity, ConfiguredVaccine, VaccinationRevision, TankGroup, Tank } from "@/types/v2";

export interface VaccineConfiguration extends BaseEntity {
  vaccines: ConfiguredVaccine[];
  sourceTank: Tank;
  sourceGroup: TankGroup;
  targetAmount: number;
  targetWeightAmount?: number;
  destinationTank: Tank;
  destinationGroup: TankGroup;
  availableGroups?: TankGroup[];
  selectedGroup?: TankGroup;
  progressData?: Array<VaccinationProgressEntry>;
  instructions?: string;
}

export interface BathVaccineConfiguration extends BaseEntity {
  vaccines: ConfiguredVaccine[];
  targetAmount: number;
  targetWeightAmount?: number;
  progressData?: Array<VaccinationProgressEntry>;
  instructions?: string;
  blindingCode?: string;
  requiresWeighing: boolean;
  challengeDuration: number;

  // Tank configurations
  tank?: Tank;              // For simplified bath
  sourceTank?: Tank;        // For standard bath
  destinationTank?: Tank;   // For standard bath
  sourceGroup?: TankGroup;  // For standard bath
  destinationGroup?: TankGroup; // For standard bath

  isSimplifiedBath: boolean;
  isStandardBath: boolean;

  // Process tracking
  bathProcess?: {
    startedAt?: string;
    completedAt?: string;
  };
}

export type VaccinationConfiguration = VaccineConfiguration | BathVaccineConfiguration;

export function isStandardVaccinationConfiguration(
  config: VaccinationConfiguration
): config is VaccineConfiguration {
  return !('isSimplifiedBath' in config || 'isStandardBath' in config);
}

export function isBathVaccinationConfiguration(
  config: VaccinationConfiguration
): config is BathVaccineConfiguration {
  return 'isSimplifiedBath' in config || 'isStandardBath' in config;
}

export function getVaccinationConfigurationInstructions(
  config: VaccinationConfiguration
): string | undefined {
  const anyConfig = config as any;
  const candidates = [
    anyConfig.instructions,
    anyConfig.specialInstructions,
    anyConfig.specialInstruction,
    anyConfig.special_instructions,
    anyConfig.special_instruction,
    anyConfig.instruction,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.length > 0) {
      return candidate;
    }
  }

  return undefined;
}

export function isSimplifiedBathVaccination(
  config: VaccinationConfiguration
): config is BathVaccineConfiguration {
  return isBathVaccinationConfiguration(config) &&
    config.isSimplifiedBath &&
    !!config.tank;
}

export function isStandardBathVaccination(
  config: VaccinationConfiguration
): config is BathVaccineConfiguration {
  return isBathVaccinationConfiguration(config) &&
    config.isStandardBath &&
    !!(config.sourceTank && config.destinationTank);
}

export interface VaccinationProgressEntry {
  vaccinated: number;
  weight: number;
  comment?: string;
  timestamp?: string;
  revisions?: Array<VaccinationRevision>;
  administrationEquipment?: string;
}
